import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PublicLayout } from "@/components/layout/PublicLayout";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Session from "./pages/Session";
import Phase2 from "./pages/Phase2";
import Phase3 from "./pages/Phase3";
import Report from "./pages/Report";
import Hypotheses from "./pages/Hypotheses";
import ActionTasks from "./pages/ActionTasks";
import Welcome from "./pages/Welcome";
import Team from "./pages/settings/Team";
import Profile from "./pages/settings/Profile";

import Seat from "./pages/settings/Seat";
import Notifications from "./pages/settings/Notifications";
import Security from "./pages/settings/Security";
import WorkspaceSettings from "./pages/settings/Workspace";
import { SettingsLayout } from "./components/settings/SettingsLayout";
import InviteAccept from "./pages/InviteAccept";
import EmailConfirmation from "./pages/EmailConfirmation";
import Privacy from "./pages/legal/Privacy";
import Imprint from "./pages/legal/Imprint";
import Terms from "./pages/legal/Terms";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          {/* Public routes with footer */}
          <Route path="/" element={<PublicLayout><Index /></PublicLayout>} />
          <Route path="/auth" element={<PublicLayout><Auth /></PublicLayout>} />
          <Route path="/auth/callback" element={<PublicLayout><AuthCallback /></PublicLayout>} />
          <Route path="/email-confirmation" element={<PublicLayout><EmailConfirmation /></PublicLayout>} />
          <Route path="/invite/:token" element={<PublicLayout><InviteAccept /></PublicLayout>} />
          <Route path="/onboarding" element={<PublicLayout><ProtectedRoute><Onboarding /></ProtectedRoute></PublicLayout>} />
          
          {/* Legal pages with footer */}
          <Route path="/legal/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
          <Route path="/legal/imprint" element={<PublicLayout><Imprint /></PublicLayout>} />
          <Route path="/legal/terms" element={<PublicLayout><Terms /></PublicLayout>} />
          
          {/* Protected app routes without footer */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/app/session/:sessionId" element={<ProtectedRoute><Session /></ProtectedRoute>} />
          <Route path="/app/session/:sessionId/phase2" element={<ProtectedRoute><Phase2 /></ProtectedRoute>} />
          <Route path="/app/session/:sessionId/phase3" element={<ProtectedRoute><Phase3 /></ProtectedRoute>} />
          <Route path="/app/session/:sessionId/report" element={<ProtectedRoute><Report /></ProtectedRoute>} />
          <Route path="/hypotheses" element={<ProtectedRoute><Hypotheses /></ProtectedRoute>} />
          <Route path="/app/tasks" element={<ProtectedRoute><ActionTasks /></ProtectedRoute>} />
          <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
          
          {/* Settings routes with unified layout */}
          <Route path="/settings" element={<Navigate to="/settings/workspace" replace />} />
          <Route path="/settings/workspace" element={<ProtectedRoute><SettingsLayout><WorkspaceSettings /></SettingsLayout></ProtectedRoute>} />
          <Route path="/settings/team" element={<ProtectedRoute><SettingsLayout><Team /></SettingsLayout></ProtectedRoute>} />
          <Route path="/settings/profile" element={<ProtectedRoute><SettingsLayout><Profile /></SettingsLayout></ProtectedRoute>} />
          
          <Route path="/settings/seat" element={<ProtectedRoute><SettingsLayout><Seat /></SettingsLayout></ProtectedRoute>} />
          <Route path="/settings/security" element={<ProtectedRoute><SettingsLayout><Security /></SettingsLayout></ProtectedRoute>} />
          <Route path="/settings/notifications" element={<ProtectedRoute><SettingsLayout><Notifications /></SettingsLayout></ProtectedRoute>} />
          
          {/* Catch-all route */}
          <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
